/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { environment } from '@env/environment';
import Echo from 'laravel-echo/dist/echo';
import { PusherChannel, PusherPrivateChannel } from 'laravel-echo/dist/channel';
import { Logger } from '@app/core/logger.service';
import {
  PushNotificationOptions,
  PushNotificationService,
} from '@app/core/push-notifications/push-notifications.service';
import { LaravelModelsEnum, Model } from '@app/shared/models/model.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import * as moment from 'moment';

const log = new Logger('WebSocketsService');

interface NotificationInterface {
  id: string;
  type: string;
  message?: string;
  name?: string;
}

export class UserState extends Model {
  public userId: number;
  public userNames: string;
  public current_location: string;
  public moment: string;
  public modelType: LaravelModelsEnum;
  public modelId: number;

  public action: string;

  // private _authService: AuthenticationService;

  constructor(model?: LaravelModelsEnum, id?: number, authService?: AuthenticationService) {
    super();

    this.modelType = model;
    this.modelId = id;

    this.current_location = window.location.pathname;
    this.moment = moment().format();

    if (authService) {
      this.userId = authService.accessToken.profile.id;
      this.userNames =
        authService.accessToken.profile.fake_first_name + ' ' + authService.accessToken.profile.fake_last_name;
    }
  }

  /**
   *
   */
  isExpired(): boolean {
    return moment().subtract(10, 'minutes').isAfter(moment(this.moment));
  }

  /**
   *
   */
  setExpired(): void {
    this.moment = moment().subtract(10, 'minutes').format();
  }

  /**
   *
   * @param model model
   * @param id id
   * @param userId userId
   */
  isSame(model: LaravelModelsEnum, id: number, userId: number) {
    return this.modelType === model && this.modelId === id && this.userId === userId;
  }
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketsService {
  // https://stackoverflow.com/questions/34376854/delegation-eventemitter-or-observable-in-angular/35568924#35568924
  reminderNotif = new BehaviorSubject<string>('');
  reminderNotifStream = this.reminderNotif.asObservable();

  public privateCommentsChannel: PusherPrivateChannel;
  // @ts-ignore
  private _socket = new Echo({ broadcaster: 'null' });
  private privateUserChannel: PusherPrivateChannel;
  private privateUserStatesChannel: PusherPrivateChannel;
  private publicChannel: PusherChannel;
  private isConnectedStatus = false;

  /**
   * User states container
   */
  private _userStatesContainerSubject = new BehaviorSubject<UserState[]>([]);
  private _userStatesContainer: UserState[] = [];

  get userStatesContainer(): Observable<UserState[]> {
    return this._userStatesContainerSubject.asObservable();
  }

  constructor(private auth: AuthenticationService, private pushNotificationService: PushNotificationService) {
    log.debug('Init...');
  }

  /**
   * Is connected to socket server?
   */
  public isConnected() {
    return this.isConnectedStatus && this._socket;
  }

  /**
   * Disconnect to socket server
   */
  public disconnect() {
    log.debug('Disconnect...');

    if (this.isConnected()) {
      this._socket.disconnect();
    }

    this.isConnectedStatus = false;
  }

  /**
   * Connect to socket server
   */
  public connect() {
    log.debug('Connect...');

    if (this.isConnected()) {
      throw Error('Already connected to socket server!');
    }

    if (!this.auth.isAuthenticated()) {
      log.error('User is not authenticated...');
      this.disconnect();
      return false;
    }

    // @ts-ignore
    this._socket = new Echo({
      // @ts-ignore
      broadcaster: environment.websockets.broadcaster,
      key: environment.websockets.key,
      wsHost: environment.websockets.wsHost,
      httpHost: environment.websockets.httpHost,
      authEndpoint: environment.websockets.authEndpoint,
      wsPort: environment.websockets.wsPort,
      wssPort: environment.websockets.wssPort,
      forceTLS: environment.websockets.forceTLS,
      disableStats: true,
      enabledTransports: ['ws', 'wss'],
      auth: {
        headers: {
          Authorization: this.auth.accessToken.token_type + ' ' + this.auth.accessToken.access_token,
        },
      },
    });

    /*
    Private user channel
     */
    this.privateUserChannel = <PusherPrivateChannel>(
      this._socket.private('App.Models.User.' + this.auth.accessToken.profile.id)
    );
    this.privateUserChannel.notification((notification: NotificationInterface) => {
      log.debug('Notification received private:', notification);
      this.show(notification);
    });

    /*
    Private user states channel
     */
    this.privateUserStatesChannel = <PusherPrivateChannel>this._socket.private('App.Models.User.States');
    this.privateUserStatesChannel.listenForWhisper('user_states', (data: UserState[]) => this.userStateSync(data));

    /*
    Public channel
     */
    this.publicChannel = <PusherChannel>this._socket.channel('public');
    this.publicChannel.notification((notification: NotificationInterface) => {
      log.debug('Notification received public:', notification);
      this.show(notification);
    });
    this.publicChannel.listen('.messages', (e: any) => {
      log.debug('PublicChannel listen', e);
    });

    this.isConnectedStatus = true;
  }

  /**
   *
   * @param model LaravelModelsEnum.
   * @param id ID of model.
   * @param callback callback to execute after subscribe.
   */
  subscribeForComments(model: LaravelModelsEnum, id: number, callback: () => void): Observable<PusherPrivateChannel> {
    log.debug('Subscribe to private comment channel: ' + model + ' -> ' + id);
    this.privateCommentsChannel = <PusherPrivateChannel>(
      this._socket.private('App.Models.Comment.' + model.toString().replace(/\\/gim, '-') + '.' + id)
    );
    this.privateCommentsChannel.notification((notification: NotificationInterface) => {
      log.debug('Notification received private comment:', notification);
      callback();
    });

    return of(this.privateCommentsChannel);
  }

  /**
   * Pop own state
   * @param model model
   * @param id id
   */
  public userStatePop(model: LaravelModelsEnum, id: number) {
    /*
    Mark own state as expired for remove from other users
     */
    this._userStatesContainer.map((userState: UserState) => {
      if (userState.isSame(model, id, this.auth.accessToken.profile.id)) {
        log.info('userStatePop setExpired');
        userState.setExpired();
      }

      return userState;
    });

    log.info('userStatePop -> send ' + model + ', ' + id, this._userStatesContainer);

    /*
    Send states to other users
     */
    this.privateUserStatesChannel.whisper('user_states', this._userStatesContainer);

    /*
    Remove states for user current data
     */
    this._userStatesContainer = this._userStatesContainer.filter((item: UserState) => !item.isExpired());

    /*
    Send states to components
     */
    this._userStatesContainerSubject.next(this._userStatesContainer);

    log.info('userStatePop -> result ' + model + ', ' + id, this._userStatesContainer);
  }

  /**
   * Push own user state
   * @param model model
   * @param id id
   */
  public userStatePush(model: LaravelModelsEnum, id: number) {
    this._userStatesContainer.push(new UserState(model, id, this.auth));

    /*
      Remove expired user states
     */
    this._userStatesContainer = this._userStatesContainer.filter((userState: UserState) => {
      return !userState.isExpired();
    });

    log.info('userStatePush -> ' + model + ', ' + id, this._userStatesContainer);

    /*
    Send states to components
     */
    this._userStatesContainerSubject.next(this._userStatesContainer);

    /*
    Send states to other users
     */
    this.privateUserStatesChannel.whisper('user_states', this._userStatesContainer);
  }

  /**
   * Show notification to user
   * @param notification NotificationInterface
   */
  private show(notification: NotificationInterface | any): void {
    let title = notification.type;
    let path: string | null = null;

    const data = notification.data ?? notification;

    const options = new PushNotificationOptions();
    options.body = 'Unknown notification type';
    options.icon = window.location.origin + '/assets/logo-32x32-notification.png';
    options.tag = 'notification-' + notification.id;

    switch (notification.type) {
      case 'App\\Notifications\\UserLoggedInNotification':
        title = 'User logged in';
        options.body = `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim();
        break;

      case 'App\\Notifications\\ReminderNotification':
        title = 'New reminder';
        options.body = data.note;
        this.reminderNotif.next('new notification');
        break;

      case 'App\\Notifications\\SystemNotification':
        title = 'System notification';
        options.body = data.message;
        break;

      case 'App\\Notifications\\WebOfferFeedbackNotification':
        title = 'Web Offer Feedback Notification';
        options.body = data.title;
        path = `/sales/view?id=${data.sale_id}`;
        break;

      case 'App\\Notifications\\ReminderCompleteNotification':
        title = 'Complete reminder';
        options.body = data.note;
        break;

      case 'App\\Notifications\\External\\EnquiryCreatedNotification':
        title = 'New Reg';
        options.body =
          `New Reg ${data.company_name} in ` +
          `${data.company?.abbreviation ?? ''} website please enter.`;

        path = `/enquiries?id=${data.id}`;
        break;

      case 'App\\Notifications\\HotelBookingContactingAgentEmployeeMissingNotification':
        title = 'Hotel booking notification';
        options.body =
          data.message ??
          data.title ??
          'Contacting agent employee is missing';

        path = `/hotel-bookings/view?id=${
          data.id ?? notification.notifiable_id
        }`;
        break;
    }

    /*
     * Generic hotel-booking notifications.
     */
    if (!path && data.model === 'App\\Models\\HotelBooking') {
      path = `/hotel-bookings/view?id=${data.id}`;
    }

    /*
     * Tell the notifications dropdown to refresh.
     */
    this.reminderNotif.next('new notification');

    this.pushNotificationService.create(title, options).subscribe(
      (notif) => {
        if (path) {
          notif.notification.onclick = () => {
            window.focus();
            window.location.href = path;
            notif.notification.close();
          };
        }

        if (notif.event.type === 'show') {
          setTimeout(() => {
            notif.notification.close();
          }, 10000);
        }
      },
      () => {}
    );
  }

  /**
   * Receiving other user states
   * @param receivedData receivedData
   * @return void
   */
  private userStateSync(receivedData: UserState[]): void {
    log.info('privateUserStatesChannel...', receivedData);

    /**
     * Data is updated?
     */
    let hasUpdate = false;

    /*
    Sync received states with current states
     */
    receivedData.forEach((item: UserState) => {
      item = new UserState().deserialize(item);

      let has = false;
      this._userStatesContainer.forEach((addedItem: UserState, index: number) => {
        if (addedItem.isSame(item.modelType, item.modelId, item.userId)) {
          log.info('isSame');

          if (addedItem.moment !== item.moment) {
            hasUpdate = true;
          }

          this._userStatesContainer[index] = item;
          has = true;
        }
      });

      if (!has) {
        log.info('Push', item);
        this._userStatesContainer.push(item);
        hasUpdate = true;
      } else {
        log.info('Nothing to push :(');
      }
    });

    /*
    Remove expired states
     */
    this._userStatesContainer = this._userStatesContainer.filter((item: UserState) => {
      if (item.isExpired()) {
        hasUpdate = true;
      }
      return !item.isExpired();
    });

    /*
     Resync states with other users
     */
    if (hasUpdate) {
      this.privateUserStatesChannel.whisper('user_states', this._userStatesContainer);
    }

    /*
    Send states to components
     */
    this._userStatesContainerSubject.next(this._userStatesContainer);

    log.info('privateUserStatesChannel change user states', this._userStatesContainer);
  }
}
